import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogConfig as MatDialogConfig } from "@angular/material/legacy-dialog";
import * as _ from "lodash";
import { first, take } from "rxjs/operators";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { Guid } from "guid-typescript";

import { IUser } from "../../_models";
import { HousingService, MediaWatcherService } from "../../_services";
import { UnSavedChangesDialogComponent } from "../../_components/unSavedChangesDialog/unSavedChangesDialog.component";

@Component({
    templateUrl: "roommatesDialog.component.html",
    styleUrls: ["roommatesDialog.component.css"]
})
export class RoommatesDialogComponent implements OnInit, OnDestroy {
    roommatesFormGroup: UntypedFormGroup;
    allowEdit: boolean;
    allocationPeriod: string;
    currentUser: IUser;
    allowedRoommates: number;
    scholasticPeriodId: number;
    housingProgramId?: number;
    housingStudentApplicationId: number;
    roommateSearchResults = [];
    defaultAllowedRoommates: number;
    currentRoommates: null;
    roommateDataChanged: boolean = false;
    acceptedRoommates: number = 0;
    canAddMoreRoommates: boolean = true;
    deletedRoommateRequests: any;
    isMobile = false;
    mediaWatcherSubscription: Subscription;

    currentRoommateDetails = new BehaviorSubject<any[]>([]);
    currentRoommateDetails$: Observable<any> = this.currentRoommateDetails.asObservable();

    searchResultDetails = new BehaviorSubject<any[]>([]);
    searchResultDetails$: Observable<any> = this.searchResultDetails.asObservable();

    displayedColumns = ["id", "studentCode", "lastName", "firstName", "providerName", "email", "accept", "delete"];

    searchResultDisplayedColumns = ["id", "studentCode", "lastName", "firstName", "providerName", "email", "add"];

    constructor(private dialogRef: MatDialogRef<RoommatesDialogComponent>,
        @Inject(MAT_DIALOG_DATA) data: any,
        private formBuilder: UntypedFormBuilder,
        public dialog: MatDialog,
        private housingService: HousingService,
        private mediaWatcherService: MediaWatcherService) {
        this.currentUser = data.currentUser;
        this.allowEdit = data.allowEdit;
        this.allocationPeriod = data.allocationPeriod;
        this.allowedRoommates = data.allowedRoommates;
        this.scholasticPeriodId = data.scholasticPeriodId;
        this.housingStudentApplicationId = data.housingStudentApplicationId;
        this.defaultAllowedRoommates = data.defaultAllowedRoommates;
        this.deletedRoommateRequests = data.deletedRoommateRequests;
        this.housingProgramId = data.housingProgramId;
    }

    ngOnDestroy() {
        this.searchResultDetails.unsubscribe();
        this.mediaWatcherSubscription.unsubscribe();
    }

    ngOnInit() {
        //this.googleAnalyticsService.logPageView("/housingRoommateRequest");

        this.mediaWatcherSubscription = this.mediaWatcherService.isTabletLarge.subscribe(isMobile => {
            this.isMobile = isMobile;

            if (this.isMobile) {
                this.displayedColumns = ["studentCode", "lastName", "firstName", "providerName", "email", "accept", "delete"];

                this.searchResultDisplayedColumns = ["studentCode", "lastName", "firstName", "providerName", "email", "add"];
            } else {
                this.displayedColumns = ["id", "studentCode", "lastName", "firstName", "providerName", "email", "accept", "delete"];

                this.searchResultDisplayedColumns = ["id", "studentCode", "lastName", "firstName", "providerName", "email", "add"];
            }
        });

        this.initializeForm();

        this.housingService.getCurrentRoommates(this.currentUser.studentId, this.housingStudentApplicationId)
            .pipe(first())
            .subscribe((result: any) => {
                this.currentRoommates = result;

                _.forEach(this.currentRoommates, (f: any) => {
                    f.sortOrder = f.housingApplicationRoommateRequestStatusId === 3 ? 2 : f.housingApplicationRoommateRequestStatusId === 2 ? 3 : 1;
                    f.hasNotification = _.find(this.deletedRoommateRequests, { "id": f.id });
                });

                this.acceptedRoommates = (_.filter(this.currentRoommates, { "housingApplicationRoommateRequestStatusId": 1 }) as any).length;

                if (this.acceptedRoommates === this.allowedRoommates)
                    this.canAddMoreRoommates = false;

                this.initializeForm();

                this.onChanges();

                this.currentRoommateDetails$
                    .pipe(take(1))
                    .subscribe(() => {
                        this.currentRoommateDetails.next(_.orderBy(this.currentRoommates, ["sortOrder", "lastName"], ["asc", "desc"]));
                    });
            });
    }

    initializeForm() {
        this.roommatesFormGroup = this.formBuilder.group({
            lastName: [{ value: null, disabled: !this.allowEdit && !this.canAddMoreRoommates }],
            firstName: [{ value: null, disabled: !this.allowEdit && !this.canAddMoreRoommates }],
            studentCode: [{ value: null, disabled: !this.allowEdit && !this.canAddMoreRoommates }],
            email: [{ value: null, disabled: !this.allowEdit && !this.canAddMoreRoommates }, [Validators.email]]
        });
    }

    onChanges() {
        this.roommatesFormGroup.get("lastName").valueChanges
            .subscribe(() => {
                this.resetSearchResults();
            });

        this.roommatesFormGroup.get("firstName").valueChanges
            .subscribe(() => {
                this.resetSearchResults();
            });

        this.roommatesFormGroup.get("studentCode").valueChanges
            .subscribe(() => {
                this.resetSearchResults();
            });

        this.roommatesFormGroup.get("email").valueChanges
            .subscribe(() => {
                this.resetSearchResults();
            });
    }

    get roommatesFormControls() { return this.roommatesFormGroup.controls as any; }

    saveRoommateDetails(submitBtn: HTMLButtonElement) {
        if (this.currentRoommateDetails.value.length > 0 && this.roommateDataChanged) {
            setTimeout(() => {
                submitBtn.disabled = true;
            }, 0);

            const roommatesList = [];

            _.forEach(this.currentRoommateDetails.value, f => {
                if (f.guid || f.changed) {
                    const roommate = {
                        id: f.guid ? 0 : f.id,
                        housingStudentApplicationId: f.housingStudentApplicationId,
                        roommateHousingStudentApplicationId: f.roommateHousingStudentApplicationId,
                        housingApplicationRoommateRequestStatusId: f.housingApplicationRoommateRequestStatusId,
                        lastUpdatedByHousingStudentApplicationId: this.housingStudentApplicationId,
                        rowversion: f.guid ? null : f.rowversion
                    }

                    roommatesList.push((roommate) as any);
                }
            });

            const saveModel = {
                roommateRequests: roommatesList,
                studentId: this.currentUser.studentId,
                scholasticPeriodId: this.scholasticPeriodId,
                housingProgramId: this.housingProgramId,
                housingStudentApplicationId: this.housingStudentApplicationId,
                providerId: this.currentUser.providerId,
                defaultAllowedRoommates: this.defaultAllowedRoommates
            };

            this.housingService.insertOrUpdateRoommateData(saveModel)
                .pipe(first())
                .subscribe(() => {
                    this.dialogRef.close(true);
                });
        }
    }

    close() {
        const deletedRoommates = this.deletedRoommateRequests ? this.deletedRoommateRequests.length : 0;

        if (this.roommateDataChanged) {
            //show unsaved changes dialog
            const dialogConfig = new MatDialogConfig();

            dialogConfig.disableClose = true;
            dialogConfig.autoFocus = true;
            dialogConfig.closeOnNavigation = true;
            dialogConfig.width = this.isMobile ? "60vw" : "30vw";
            dialogConfig.height = "auto";
            dialogConfig.panelClass = "unSavedChanges-dialog-container";

            const dialogRef = this.dialog.open(UnSavedChangesDialogComponent, dialogConfig);

            dialogRef.afterClosed().subscribe(result => {
                if (result === "confirm") {
                    this.dialogRef.close(deletedRoommates > 0);
                }
            });
        } else {
            this.dialogRef.close(deletedRoommates > 0);
        }
    }

    search() {
        if (this.roommatesFormGroup.invalid) {
            return;
        }

        if (this.roommatesFormGroup.dirty) {
            const currentRoommates = _.filter(this.currentRoommateDetails.value, f => { return f.housingApplicationRoommateRequestStatusId !== 2; });

            var currentRoommateIds = _.map(currentRoommates, "roommateHousingStudentApplicationId");

            _.forEach(_.map(currentRoommates, "housingStudentApplicationId"), f => {
                currentRoommateIds.push(f);
            });

            const saveModel = {
                housingStudentApplicationId: this.housingStudentApplicationId,
                scholasticPeriodId: this.scholasticPeriodId,
                housingProgramId: this.housingProgramId,
                genderTypeId: this.currentUser.gender,
                firstName: this.roommatesFormControls.firstName.value,
                lastName: this.roommatesFormControls.lastName.value,
                studentCode: this.roommatesFormControls.studentCode.value,
                email: this.roommatesFormControls.email.value,
                defaultAllowedRoommates: this.defaultAllowedRoommates,
                currentRoommateHousingStudentApplicationIds: currentRoommateIds
            };

            this.housingService.roommateSearch(saveModel)
                .pipe(first())
                .subscribe((result: any) => {
                    this.roommateSearchResults = result;

                    this.searchResultDetails$
                        .pipe(take(1))
                        .subscribe(() => {
                            this.searchResultDetails.next(_.orderBy(this.roommateSearchResults, ["lastName"], ["asc"]));
                        });
                });
        }
    }

    resetSearchResults() {
        this.searchResultDetails$
            .pipe(take(1))
            .subscribe(() => {
                this.searchResultDetails.next([]);
            });
    }

    addRoommate(roommateHousingStudentApplicationId: any) {
        var roommate = _.find(this.searchResultDetails.value, { "housingStudentApplicationId": roommateHousingStudentApplicationId });

        if (roommate) {
            const index = this.currentRoommateDetails.value.length === 0 ? 0 : _.maxBy(this.currentRoommateDetails.value, "id").id + 1;

            this.currentRoommateDetails$
                .pipe(take(1))
                .subscribe(val => {
                    const roommateData = [...val, {
                        id: index,
                        housingStudentApplicationId: this.housingStudentApplicationId,
                        roommateHousingStudentApplicationId: roommateHousingStudentApplicationId,
                        housingApplicationRoommateRequestStatusId: 3,
                        firstName: roommate.firstName,
                        lastName: roommate.lastName,
                        studentCode: roommate.studentCode,
                        email: roommate.email,
                        providerName: roommate.providerName,
                        sortOrder: 2,
                        guid: Guid.create()
                    }];

                    this.currentRoommateDetails.next(_.orderBy(roommateData, ["sortOrder", "lastName"], ["asc", "desc"]));

                    roommate.alreadyAdded = true;

                    this.roommateDataChanged = true;

                    //clear search results if all found students have been added as room mates
                    if (!this.searchResultDetails.value.find(f => !f.alreadyAdded)) {
                        this.resetSearchResults();
                        this.roommatesFormGroup.reset();
                    }
                });
        }
    }

    deleteRoommate(id: any) {
        this.currentRoommateDetails$
            .pipe(take(1))
            .subscribe(val => {
                const roommateData = [...val];

                var index = roommateData.findIndex((r) => r.id === id);

                var deletedRoommate = roommateData[index];

                if (deletedRoommate.guid) {
                    roommateData.splice(index, 1);

                    this.currentRoommateDetails.next(_.orderBy(roommateData, ["sortOrder", "lastName"], ["asc", "desc"]));

                    const roommate = _.find(this.searchResultDetails.value, { "housingStudentApplicationId": deletedRoommate.roommateHousingStudentApplicationId });

                    if (roommate)
                        roommate.alreadyAdded = false;
                } else {
                    if (deletedRoommate.housingApplicationRoommateRequestStatusId === 1) {
                        this.acceptedRoommates -= 1;

                        if (this.acceptedRoommates < this.allowedRoommates)
                            this.canAddMoreRoommates = true;
                    }

                    deletedRoommate.housingApplicationRoommateRequestStatusId = 2;
                    deletedRoommate.changed = true;

                    this.roommateDataChanged = true;
                }
            });
    }

    acceptRoommate(id: any) {
        const roommate = _.find(this.currentRoommateDetails.value, { "id": id });
        roommate.housingApplicationRoommateRequestStatusId = 1;
        roommate.sortOrder = 1;
        roommate.changed = true;

        this.acceptedRoommates += 1;

        if (this.acceptedRoommates === this.allowedRoommates) {
            this.canAddMoreRoommates = false;

            this.resetSearchResults();
        }

        this.currentRoommateDetails$
            .pipe(take(1))
            .subscribe(val => {
                const roommateData = [...val];

                this.currentRoommateDetails.next(_.orderBy(roommateData, ["sortOrder", "lastName"], ["asc", "desc"]));
            });

        this.roommateDataChanged = true;
    }
}